import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Bot, User, Sparkles, Maximize2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import SERVER_URL from "../../config/server";

const AIAssistant = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Hi there! I'm Chahat's Assistant. Ask me anything about his technical experience, Salesforce certifications, or AI projects!",
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputText, setInputText] = useState("");
  const [hiringFlow, setHiringFlow] = useState(null);
  const messagesEndRef = useRef(null);

  const quickPrompts = [
    "Show Salesforce experience",
    "What is your AI expertise?",
    "Explain StockPilot architecture",
    "Are you available for remote SDE roles?"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  const handleToggleChat = () => {
    setIsOpen(!isOpen);
  };

  const callGeminiDirectAPI = async (message, history, apiKey) => {
    const systemInstruction = `You are Chahat's AI Copilot, a helpful and professional AI assistant on Chahat Jain's portfolio website. 
Your goal is to answer questions from recruiters and visitors about Chahat's technical skills, experience, projects, and availability.

Here is the context about Chahat:
- Name: Chahat Jain
- Role: Senior Full Stack SDE & Salesforce AI Automation Engineer
- Salesforce Certification: Certified Platform Developer I (PD1) and Agentforce Specialist
- Experience:
  1. Associate Software Developer at Twopir Consulting (built Apex automations, LWC, automated workflows integrating Salesforce with HubSpot & external REST APIs using n8n with over 100k+ runs, delivering a 95% reduction in manual CRM entry).
  2. IT Head & Developer at Gujarat Institute of Development Research (developed full-stack analytics dashboard, indexing, query optimization).
  3. Salesforce Developer Intern (Apex, triggers, LWC).
  4. Software Engineer Intern at NeoDocto (React, Node.js).
- Key Projects:
  1. StockPilot: Intelligent inventory SaaS built with Next.js, TypeScript, MongoDB, and Firebase. Uses Google Genkit AI models to recommend discount rules and coupons based on sales velocity. Optimized sync speeds by 35%.
  2. Google GenAI Capstone project: A career mentor chatbot using Gemini models, prompt engineering, RAG, and grounding.
- Skills:
  - Frontend: React.js, Next.js, HTML, CSS, TailwindCSS, JavaScript, TypeScript
  - Backend: Node.js, Express.js, Python (Flask), Java, C++ (300+ algorithm challenges solved)
  - Databases: MongoDB, PostgreSQL, SQLite
  - Devops/Other: Docker, Redis, Git, REST APIs, n8n, Zapier
- Education: B.Tech in Electrical Engineering with Minors in Computer Science from Nirma University.
- Availability: Currently available for remote, hybrid, or on-site software engineering (SDE), fullstack, or Salesforce developer roles.

Guidelines:
- Keep your answers concise, clear, and professional. Do not write extremely long messages.
- Highlight his strengths in fullstack development, AI/automation, and Salesforce.
- IMPORTANT FORMATTING RULE: You MUST format your response using HTML tags only. Do NOT use markdown format (such as **, *, -, etc.). Use HTML tags exclusively:
  - <strong>bold text</strong> for titles, highlights, or key metrics.
  - <ul> and <li> tags for lists and points-wise detailed breakdowns.
  - <p> for paragraphs and <br /> for line breaks.
  - <a href="mailto:chahatj06@gmail.com" style="color: #06b6d4; text-decoration: underline;">chahatj06@gmail.com</a> for links.
- If you don't know the answer, politely invite the user to contact Chahat directly using the Contact form or email (chahatj06@gmail.com).`;

    console.log("Direct Gemini API request initiated. Model: gemini-3.1-flash-lite. Message:", message);
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite:generateContent?key=${apiKey}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            ...history.filter(m => !m.isTyping).map((msg) => ({
              role: msg.sender === "user" ? "user" : "model",
              parts: [{ text: msg.text }],
            })),
            {
              role: "user",
              parts: [{ text: message }],
            },
          ],
          systemInstruction: {
            parts: [{ text: systemInstruction }]
          }
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Gemini direct API call failed with status ${response.status}:`, errorText);
      throw new Error(`Gemini direct API call failed: ${response.statusText}`);
    }

    const data = await response.json();
    const resultText = data.candidates?.[0]?.content?.parts?.[0]?.text || "I'm sorry, I encountered an issue processing your request.";
    console.log("Direct Gemini API request completed. Response length:", resultText.length);
    return resultText;
  };

  const isQueryRelevant = (query) => {
    const q = query.toLowerCase().trim();
    
    // Basic greetings/conversational terms
    const greetings = ['hi', 'hello', 'hey', 'yo', 'sup', 'welcome', 'hola', 'greetings', 'who are you', 'what is this', 'good morning', 'good afternoon', 'good evening'];
    if (greetings.some(word => q === word || q.startsWith(word + ' ') || q.endsWith(' ' + word))) {
      return true;
    }

    // Professional portfolio and hiring keywords
    const keywords = [
      'chahat', 'jain', 'portfolio', 'resume', 'cv', 'experience', 'work', 'job', 'hire', 'hiring',
      'sde', 'developer', 'engineer', 'coding', 'programmer', 'stack', 'skills', 'contact', 'email',
      'salesforce', 'pd1', 'agentforce', 'apex', 'lwc', 'crm', 'hubspot', 'n8n', 'automation',
      'react', 'node', 'express', 'mongodb', 'postgresql', 'sqlite', 'docker', 'redis', 'next.js',
      'stockpilot', 'inventory', 'saas', 'genai', 'gemini', 'openai', 'chatbot', 'mentor', 'project',
      'twopir', 'gidr', 'neodocto', 'nirma', 'university', 'b.tech', 'education', 'degree', 'remote'
    ];

    // Programming technologies/concepts
    const generalTech = [
      'javascript', 'typescript', 'python', 'java', 'c++', 'html', 'css', 'tailwind', 'api', 'rest',
      'git', 'github', 'database', 'frontend', 'backend', 'fullstack', 'web', 'app', 'application'
    ];

    return keywords.some(kw => q.includes(kw)) || generalTech.some(kw => q.includes(kw));
  };

  const isHiringQuery = (query) => {
    const q = query.toLowerCase().trim();
    const hiringWords = ['hire', 'hiring', 'job', 'position', 'role', 'interview', 'salary', 'recruit', 'recruiter', 'recruiting', 'vacancy', 'openings', 'offer', 'work with', 'looking for a developer', 'sde role', 'contract'];
    return hiringWords.some(word => q.includes(word));
  };

  const handleSendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = {
      sender: "user",
      text: text,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");

    console.log("handleSendMessage triggered. Input text:", text);

    // 1. Intercept if in hiring flow
    if (hiringFlow) {
      console.log("Hiring flow active. Intercepting input for step:", hiringFlow.step);
      
      // Add typing/thinking delay for a natural conversational feel
      const typingMessage = {
        sender: "bot",
        text: "Thinking...",
        isTyping: true,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prev) => [...prev, typingMessage]);

      setTimeout(async () => {
        try {
          if (hiringFlow.step === "awaiting_name") {
            const name = text.trim();
            setHiringFlow({
              step: "awaiting_company",
              data: { ...hiringFlow.data, name }
            });
            // Replace typing indicator with bot question
            setMessages((prev) =>
              prev.map((msg) =>
                msg.isTyping
                  ? {
                      sender: "bot",
                      text: `Thanks, <strong>${name}</strong>! What is the name of <strong>your company or organization</strong>?`,
                      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    }
                  : msg
              )
            );
          } else if (hiringFlow.step === "awaiting_company") {
            const company = text.trim();
            setHiringFlow({
              step: "awaiting_email",
              data: { ...hiringFlow.data, company }
            });
            setMessages((prev) =>
              prev.map((msg) =>
                msg.isTyping
                  ? {
                      sender: "bot",
                      text: `Got it, <strong>${company}</strong>. What is <strong>your email address</strong> so Chahat can reach you?`,
                      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    }
                  : msg
              )
            );
          } else if (hiringFlow.step === "awaiting_email") {
            const email = text.trim();
            // Validate email format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.isTyping
                    ? {
                        sender: "bot",
                        text: `That email doesn't look quite right. Please enter a <strong>valid email address</strong>:`,
                        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      }
                    : msg
                )
              );
              return;
            }

            setHiringFlow({
              step: "awaiting_message",
              data: { ...hiringFlow.data, email }
            });
            setMessages((prev) =>
              prev.map((msg) =>
                msg.isTyping
                  ? {
                      sender: "bot",
                      text: `Thank you. Lastly, could you share a brief <strong>description or details of the role</strong> you are hiring for?`,
                      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    }
                  : msg
              )
            );
          } else if (hiringFlow.step === "awaiting_message") {
            const message = text.trim();
            const finalData = { ...hiringFlow.data, message };
            
            // Show "Saving details..." in typing indicator
            setMessages((prev) =>
              prev.map((msg) =>
                msg.isTyping ? { ...msg, text: "Saving details..." } : msg
              )
            );

            try {
              const response = await fetch(`${SERVER_URL}/publicRoute/lead`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(finalData),
              });

              if (response.ok) {
                console.log("Hiring lead successfully saved to database:", finalData);
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.isTyping
                      ? {
                          sender: "bot",
                          text: `<strong>Success!</strong> Thank you, <strong>${finalData.name}</strong>. Your details have been recorded.<br/><br/>Chahat will contact you at <strong>${finalData.email}</strong> shortly.`,
                          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        }
                      : msg
                  )
                );
              } else {
                throw new Error("Failed to submit lead");
              }
            } catch (err) {
              console.error("Failed to save lead in conversational flow:", err);
              // Fallback response to keep UX friendly
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.isTyping
                    ? {
                        sender: "bot",
                        text: `Thank you, <strong>${finalData.name}</strong>! I've noted down your details. Chahat will contact you at <strong>${finalData.email}</strong> shortly.`,
                        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      }
                    : msg
                )
              );
            } finally {
              setHiringFlow(null); // Reset hiring flow
            }
          }
        } catch (error) {
          console.error("Error in conversational lead capture step:", error);
          setHiringFlow(null);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.isTyping
                ? {
                    sender: "bot",
                    text: "I'm sorry, I ran into an issue saving your details. Let's restart. How can I help you?",
                    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  }
                : msg
            )
          );
        }
      }, 800);
      
      return;
    }

    // 2. Relevance Pre-Check
    if (!isQueryRelevant(text)) {
      console.log("Relevance check: Query is irrelevant. Replying with polite refusal.");
      const refusalMessage = {
        sender: "bot",
        text: "I'm Chahat's professional AI Copilot. I only answer questions related to Chahat Jain's experience, certifications, full-stack projects, and job inquiries. Please try asking something about Chahat's work or hiring availability!",
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setTimeout(() => {
        setMessages((prev) => [...prev, refusalMessage]);
      }, 500);
      return;
    }

    // 3. Add typing/loading indicator
    const typingMessage = {
      sender: "bot",
      text: "Thinking...",
      isTyping: true,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages((prev) => [...prev, typingMessage]);

    try {
      let botResponseText = "";
      const clientApiKey = process.env.REACT_APP_GEMINI_API_KEY;
      let success = false;

      if (clientApiKey) {
        console.log("Attempting Mode 1: Client-side direct call to Gemini API...");
        try {
          botResponseText = await callGeminiDirectAPI(text, messages, clientApiKey);
          success = true;
          console.log("Mode 1 (Direct call) succeeded.");
        } catch (directError) {
          console.error("Mode 1 (Direct call) failed, falling back to Mode 2/3:", directError);
        }
      } else {
        console.log("No clientApiKey found in REACT_APP_GEMINI_API_KEY. Skipping Mode 1.");
      }

      if (!success) {
        console.log(`Attempting Mode 2: Backend proxy call to ${SERVER_URL}/publicRoute/chat...`);
        try {
          const response = await fetch(`${SERVER_URL}/publicRoute/chat`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              message: text,
              history: messages.filter(m => !m.isTyping),
            }),
          });
          
          if (response.ok) {
            const data = await response.json();
            botResponseText = data.response;
            success = true;
            console.log("Mode 2 (Backend proxy call) succeeded.");
          } else {
            throw new Error(`Backend returned status ${response.status}: ${response.statusText}`);
          }
        } catch (backendError) {
          console.error("Mode 2 (Backend proxy call) failed, falling back to Mode 3:", backendError);
          console.log("Attempting Mode 3: Local mock response generation as final fallback...");
          botResponseText = generateBotResponse(text);
          success = true;
          console.log("Mode 3 (Local mock response) completed.");
        }
      }

      // Replace typing indicator with actual response
      setMessages((prev) =>
        prev.map((msg) =>
          msg.isTyping
            ? {
                sender: "bot",
                text: botResponseText,
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              }
            : msg
        )
      );

      // 4. Hiring Flow detection (Start conversational details request after response is displayed)
      if (isHiringQuery(text)) {
        console.log("Hiring query detected. Starting conversational lead capture flow.");
        setHiringFlow({ step: "awaiting_name", data: {} });
        setTimeout(() => {
          setMessages((prev) => [
            ...prev,
            {
              sender: "bot",
              text: "I would be glad to connect you with Chahat regarding hiring! To get started, could you please tell me <strong>your name</strong>?",
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            }
          ]);
        }, 1000);
      }
    } catch (error) {
      console.error("Critical unexpected error in AIAssistant handleSendMessage:", error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.isTyping
            ? {
                sender: "bot",
                text: "I'm sorry, I ran into an error. Please try again.",
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              }
            : msg
        )
      );
    }
  };

  const generateBotResponse = (query) => {
    const q = query.toLowerCase();

    if (q.includes("salesforce") || q.includes("apex") || q.includes("lwc") || q.includes("flow") || q.includes("crm")) {
      return "Chahat is a certified Salesforce Platform Developer I (PD1) and Agentforce Specialist with 2+ years of enterprise experience. At Twopir Consulting, he built Apex automations, Lightning Web Components, and automated workflows integrating Salesforce with HubSpot and external REST APIs, delivering a 95% reduction in manual data entry.";
    }

    if (q.includes("ai") || q.includes("automation") || q.includes("openai") || q.includes("gemini") || q.includes("n8n") || q.includes("agent") || q.includes("langchain")) {
      return "Chahat specializes in AI Engineering & Workflow Automation. He has built production-grade lead qualification chatbots using OpenAI API & n8n (over 100k+ workflow runs). He also holds a Google GenAI Capstone certification for his Gemini career mentor project, implementing grounding, prompt engineering, and RAG systems.";
    }

    if (q.includes("stockpilot") || q.includes("inventory") || q.includes("recommendation") || q.includes("next")) {
      return "StockPilot is an intelligent inventory SaaS built with Next.js, TypeScript, MongoDB, and Firebase. It uses Google Genkit AI models to recommend promotional coupons and discount rules by analyzing sales velocity. It optimized inventory sync speeds by 35% for retail users.";
    }

    if (q.includes("node") || q.includes("express") || q.includes("mern") || q.includes("react") || q.includes("fullstack") || q.includes("backend") || q.includes("frontend") || q.includes("c++")) {
      return "As a Full-Stack developer, Chahat writes React & Next.js for client interfaces, and Node.js/Express, Python/Flask, and Java for API services. He stores data in MongoDB, PostgreSQL, and SQLite, and Dockerizes services to ensure development parity. He has solved over 300+ algorithm challenges in C++.";
    }

    if (q.includes("remote") || q.includes("hire") || q.includes("job") || q.includes("available") || q.includes("status") || q.includes("work")) {
      return "Chahat is available for Full-Time software engineering, Full-Stack React/Node, Salesforce development, or AI Automation developer roles. He is open to Remote, Hybrid, or On-site positions and holds a B.Tech in Electrical Engineering with Minors in Computer Science from Nirma University.";
    }

    if (q.includes("experience") || q.includes("history") || q.includes("twopir") || q.includes("gidr")) {
      return "Chahat's experience includes: 1) Associate Software Developer at Twopir Consulting (AI workflows, CRM sync, REST APIs, n8n); 2) IT Head & Developer at Gujarat Institute of Development Research (full-stack analytics, database indexing); 3) Salesforce Developer Intern (Apex, triggers, LWC); 4) NeoDocto Intern (React/Node).";
    }

    if (q.includes("chahat") || q.includes("about") || q.includes("who is") || q.includes("tell me more")) {
      return "Chahat Jain is a Full-Stack Software Developer & Salesforce AI Automation Engineer with over 2 years of professional coding experience. He is a certified Salesforce Platform Developer I (PD1) and Agentforce Specialist. Chahat specializes in building MERN/Next.js applications, custom AI integrations, and CRM workflow automation. Would you like to know more about his projects, experience, or certifications?";
    }

    if (q.includes("hello") || q.includes("hi") || q.includes("hey") || q.includes("welcome")) {
      return "Hello! I can tell you about Chahat's software experience, full-stack web development, Salesforce automation, or AI projects. How can I help you today?";
    }

    return "I'm Chahat's custom assistant. I can answer questions about his 2+ years of enterprise coding, fullstack React/Node projects (like StockPilot), AI pipelines (n8n/OpenAI), and Salesforce certifications. Try clicking one of the quick prompts below!";
  };

  if (location.pathname === "/chat") {
    return null;
  }

  return (
    <>
      {/* Floating Chat Launcher Button */}
      <motion.button
        onClick={handleToggleChat}
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-6 right-6 z-50 p-4 bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-full shadow-2xl text-white border border-white/20 cursor-pointer flex items-center justify-center"
        style={{ boxShadow: "0 0 25px rgba(6, 182, 212, 0.4)" }}
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
        {!isOpen && (
          <span className="absolute top-0 right-0 flex h-3.5 w-3.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-cyan-500"></span>
          </span>
        )}
      </motion.button>

      {/* Custom AI Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="fixed bottom-24 right-4 md:right-6 z-50 w-[calc(100%-2rem)] max-w-[360px] md:max-w-[400px] h-[450px] md:h-[500px] max-h-[calc(100vh-120px)] bg-slate-950/90 border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col backdrop-blur-xl"
            style={{ boxShadow: "0 10px 40px rgba(0,0,0,0.5)" }}
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-white/10 bg-gradient-to-r from-cyan-900/40 via-blue-900/40 to-indigo-900/40 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="p-2 bg-cyan-500/20 rounded-xl border border-cyan-400/30">
                  <Sparkles className="w-5 h-5 text-cyan-400 animate-pulse" />
                </span>
                <div>
                  <h3 className="font-bold text-white text-sm tracking-wide">
                    Chahat's AI Copilot
                  </h3>
                  <span className="text-[10px] text-cyan-400 font-semibold uppercase tracking-wider flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block animate-ping"></span>
                    Online & Active
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    navigate("/chat", { state: { messages, hiringFlow } });
                    setIsOpen(false);
                  }}
                  className="text-gray-400 hover:text-white transition cursor-pointer"
                  title="Expand to Full Chat"
                >
                  <Maximize2 className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-white transition cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#080e1a]/40">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-2.5 ${msg.sender === "user" ? "flex-row-reverse" : ""}`}
                >
                  <div
                    className={`p-2 rounded-xl border shrink-0 flex items-center justify-center ${
                      msg.sender === "user"
                        ? "bg-indigo-500/20 border-indigo-400/20 text-indigo-400"
                        : "bg-cyan-500/20 border-cyan-400/20 text-cyan-400"
                    }`}
                  >
                    {msg.sender === "user" ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>
                  <div className="max-w-[75%] flex flex-col">
                    <div
                      className={`px-4 py-2.5 rounded-2xl text-xs leading-relaxed font-medium ${
                        msg.sender === "user"
                          ? "bg-indigo-600 text-white rounded-tr-none"
                          : "bg-white/5 border border-white/10 text-gray-200 rounded-tl-none"
                      }`}
                    >
                      {msg.sender === "bot" ? (
                        <div dangerouslySetInnerHTML={{ __html: msg.text }} />
                      ) : (
                        msg.text
                      )}
                    </div>
                    <span className={`text-[9px] text-gray-500 mt-1 ${msg.sender === "user" ? "text-right" : ""}`}>
                      {msg.time}
                    </span>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} className="h-4" />
            </div>

            {/* Quick Actions Panel */}
            <div className="px-4 py-2 bg-slate-950 border-t border-white/5 flex flex-wrap gap-1.5 justify-center">
              {quickPrompts.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSendMessage(prompt)}
                  className="text-[10px] font-semibold text-cyan-300 hover:text-white bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-400/20 px-2.5 py-1.5 rounded-lg transition-all text-left cursor-pointer"
                >
                  {prompt}
                </button>
              ))}
            </div>

            {/* Input Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage(inputText);
              }}
              className="p-3 border-t border-white/10 bg-slate-950 flex gap-2"
            >
              <input
                type="text"
                placeholder="Ask about skills, projects, stack..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 transition"
              />
              <button
                type="submit"
                className="p-2 bg-cyan-500 text-slate-900 rounded-xl hover:bg-cyan-400 transition cursor-pointer flex items-center justify-center"
              >
                <Send className="w-4 h-4 text-slate-950" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIAssistant;
